import React, { useMemo } from 'react';
import {
    BarChart, Bar, PieChart, Pie,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell
} from 'recharts';
import type { FinanceRecord } from '../../types/newFinance';
import { EXPENSE_CATEGORY_LABELS } from '../../types/newFinance';

interface Props {
    records: FinanceRecord[];
    selectedMonth: number;
}

const COLORS = [
    '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
    '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1',
    '#14b8a6', '#f43f5e'
];

export const MonthlyFinanceCharts: React.FC<Props> = ({ records, selectedMonth }) => {
    // 주차별 수입/지출 데이터
    const weeklyTrendData = useMemo(() => {
        const data: Array<{
            week: string;
            수입: number;
            지출: number;
            순이익: number;
        }> = [];

        // 주차 계산 헬퍼
        const getWeekNumber = (date: Date) => {
            const firstDayOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
            const pastDaysOfMonth = (date.getTime() - firstDayOfMonth.getTime()) / 86400000;
            return Math.ceil((pastDaysOfMonth + firstDayOfMonth.getDay() + 1) / 7);
        };

        // 주차별 데이터 초기화 (최대 6주)
        for (let i = 1; i <= 6; i++) {
            data.push({ week: `${i}주차`, 수입: 0, 지출: 0, 순이익: 0 });
        }

        records.forEach(record => {
            const date = new Date(record.date);
            const week = getWeekNumber(date);

            if (week >= 1 && week <= 6) {
                // HUF 기준 합산
                const income = record.incomeTotals.HUF || 0;
                const expense = record.expenseTotals.HUF || 0;

                data[week - 1].수입 += income;
                data[week - 1].지출 += expense;
                data[week - 1].순이익 += (income - expense);
            }
        });

        // 데이터가 있는 주차만 필터링
        return data.filter(item => item.수입 > 0 || item.지출 > 0);
    }, [records]);

    // 카테고리별 지출 데이터
    const expenseCategoryData = useMemo(() => {
        const categoryTotals: { [key: string]: number } = {};

        records.forEach(record => {
            record.expenseItems.forEach(item => {
                const categoryLabel = EXPENSE_CATEGORY_LABELS[item.category];
                if (!categoryTotals[categoryLabel]) {
                    categoryTotals[categoryLabel] = 0;
                }
                if (item.currency === 'HUF') {
                    categoryTotals[categoryLabel] += item.amount;
                }
            });
        });

        return Object.entries(categoryTotals)
            .map(([name, value]) => ({ name, value }))
            .filter(item => item.value > 0)
            .sort((a, b) => b.value - a.value);
    }, [records]);

    // 수입 종류별 데이터
    const incomeTypeData = useMemo(() => {
        let titheTotal = 0;
        let thanksgivingTotal = 0;
        let otherTotal = 0;

        records.forEach(record => {
            record.titheItems.forEach(item => item.currency === 'HUF' && (titheTotal += item.amount));
            record.thanksgivingItems.forEach(item => item.currency === 'HUF' && (thanksgivingTotal += item.amount));
            record.otherIncomeItems.forEach(item => item.currency === 'HUF' && (otherTotal += item.amount));
        });

        return [
            { name: '십일조', value: titheTotal },
            { name: '감사헌금', value: thanksgivingTotal },
            { name: '기타헌금', value: otherTotal }
        ].filter(item => item.value > 0);
    }, [records]);

    // 커스텀 툴팁
    const CustomTooltip = ({ active, payload, label }: any) => {
        if (active && payload && payload.length) {
            return (
                <div className="bg-white dark:bg-gray-800 p-3 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 text-sm">
                    <p className="font-bold text-gray-900 dark:text-white mb-2">{label}</p>
                    {payload.map((entry: any, index: number) => (
                        <p key={index} style={{ color: entry.color }} className="mb-1">
                            {entry.name}: {entry.value.toLocaleString()} HUF
                        </p>
                    ))}
                </div>
            );
        }
        return null;
    };

    const PieTooltip = ({ active, payload }: any) => {
        if (active && payload && payload.length) {
            const data = payload[0];
            return (
                <div className="bg-white dark:bg-gray-800 p-3 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 text-sm">
                    <p className="font-bold text-gray-900 dark:text-white mb-1">{data.name}</p>
                    <p className="text-gray-700 dark:text-gray-300">
                        {data.value.toLocaleString()} HUF
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {((data.value / data.payload.total) * 100).toFixed(1)}%
                    </p>
                </div>
            );
        }
        return null;
    };

    return (
        <div className="space-y-6 mt-6 animate-fadeIn">
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    📅 {selectedMonth}월 주차별 흐름
                </h3>
                <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={weeklyTrendData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" opacity={0.1} />
                        <XAxis dataKey="week" stroke="#6b7280" fontSize={12} />
                        <YAxis stroke="#6b7280" fontSize={12} />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend wrapperStyle={{ fontSize: '12px' }} />
                        <Bar dataKey="수입" fill="#10b981" radius={[4, 4, 0, 0]} barSize={20} />
                        <Bar dataKey="지출" fill="#ef4444" radius={[4, 4, 0, 0]} barSize={20} />
                    </BarChart>
                </ResponsiveContainer>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {incomeTypeData.length > 0 && (
                    <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                            💰 수입 구성
                        </h3>
                        <ResponsiveContainer width="100%" height={250}>
                            <PieChart>
                                <Pie
                                    data={incomeTypeData}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={50}
                                    outerRadius={80}
                                    paddingAngle={2}
                                    dataKey="value"
                                >
                                    {incomeTypeData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip content={<PieTooltip />} />
                                <Legend wrapperStyle={{ fontSize: '12px' }} />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                )}

                {expenseCategoryData.length > 0 && (
                    <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                            💸 지출 구성
                        </h3>
                        <ResponsiveContainer width="100%" height={250}>
                            <PieChart>
                                <Pie
                                    data={expenseCategoryData}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={50}
                                    outerRadius={80}
                                    paddingAngle={2}
                                    dataKey="value"
                                >
                                    {expenseCategoryData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                    ))}
                                </Pie>
                                <Tooltip content={<PieTooltip />} />
                                <Legend iconSize={8} wrapperStyle={{ fontSize: '12px' }} />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                )}
            </div>
        </div>
    );
};
